import React from 'react';
import styled from 'styled-components/native';
import Icon from '../../icon';
import {useTheme} from 'styled-components';
import {Theme} from '../../../theme/lightTheme';
import {DrawerActions, useNavigation} from '@react-navigation/native';
import {MenuIconProps} from '../../../interfaces/components/header';

// Styled container for the menu icon
const Container = styled.View``;

/**
 * MenuIcon Component
 * @param {Object} props - Component props
 * @param {Function} props.onPress - Callback function to be executed when the icon is pressed.
 * @param {string} props.iconName - Name of the icon. Default is 'menu'.
 * @param {string} props.iconType - Type of the icon. Default is 'SVG'.
 * @param {number} props.iconSize - Size of the icon. Default is obtained from the theme.
 * @param {string} props.tintColor - Color of the icon. Default is obtained from the theme.
 * @returns {React.ReactElement} - Rendered component.
 */
const MenuIcon = ({
  onPress,
  iconName,
  iconType,
  iconSize,
  tintColor,
  role = 'button',
}: MenuIconProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const navigation = useNavigation();

  /**
   * Handles the press event for the menu icon.
   * If `onPress` prop is provided, executes the callback.
   * Otherwise, dispatches the toggleDrawer action to open/close the navigation drawer.
   */
  const onPressHandler = () => {
    if (onPress) {
      onPress();
      return;
    }
    navigation?.dispatch(DrawerActions.toggleDrawer());
  };

  // If navigation is not available, render an empty container
  //@ts-ignore
  if (!navigation?.toggleDrawer) {
    return <Container />;
  }

  return (
    <Container>
      {/* Icon component representing the menu icon */}
      <Icon
        name={iconName || 'menu'} // Replace with the actual icon name
        color={tintColor || theme.text.light}
        size={iconSize || theme.text.s5}
        role={role}
        onPress={onPressHandler}
      />
    </Container>
  );
};

// Default props for MenuIcon component
MenuIcon.defaultProps = {
  onPress: null,
};

export default MenuIcon;
